import { QRCodeSVG, QRCodeCanvas } from 'qrcode.react'
import { Component, createRef } from 'react'

import type { ICourseData } from '../../../utils/interfaces/interfaces'
import type { SchoolConfig } from '../../../utils/hooks/useSchool'
import { Transition } from '@headlessui/react'

interface PCourseCard {
    course: ICourseData
    school: SchoolConfig
}

interface SCourseCard {
    showing_details: boolean
    banner: string
    qrImageUrl: string
}

class CourseCard extends Component<PCourseCard, SCourseCard> {
    // state declaration
    state: SCourseCard = {
        showing_details: false,
        qrImageUrl: '',

        // Note: all terms that ends with 01 as term value doesn't need a banner
        banner: !this.props.course.school_name_and_term.includes('01')
            ? this.props.course.course_id
            : ''
    }

    canvasRef = createRef<HTMLDivElement>()

    // constants
    SHOW_ID = !this.props.course.school_name_and_term.includes('01')

    ANIMATION_SPEED = 30

    // animation functions
    banner_text_removal = (new_text: string) => {
        let current_text = this.state.banner
        let length = current_text.length
        if (length > 0) {
            this.setState({
                ...this.state,
                banner: current_text.slice(0, length - 1)
            })

            setTimeout(
                () => this.banner_text_removal(new_text),
                this.ANIMATION_SPEED
            )
        } else {
            this.banner_text_animation(0, new_text)
        }
    }

    banner_text_animation = (index: number, new_text: string) => {
        let current_text = this.state.banner

        if (index < new_text.length) {
            let charToAdd = new_text.charAt(index)

            this.setState({
                ...this.state,
                banner: current_text + charToAdd
            })

            setTimeout(() => {
                this.banner_text_animation(++index, new_text)
            }, this.ANIMATION_SPEED)
        }
    }

    banner_id_to_name = () => {
        this.banner_text_removal(
            this.props.course.course_name.replace('/', ' / ')
        )
    }

    banner_name_to_id = () => {
        this.banner_text_removal(
            this.SHOW_ID ? this.props.course.course_id : ''
        )
    }

    // Convert hidden canvas QR to image URL for long-press save on mobile
    generateQrImage = () => {
        setTimeout(() => {
            const canvas = this.canvasRef.current?.querySelector('canvas')
            if (canvas) {
                this.setState({ qrImageUrl: canvas.toDataURL('image/png') })
            }
        }, 50)
    }

    // onClick handler
    card_on_click_handler = () => {
        if (!this.state.showing_details) {
            this.setState({ ...this.state, showing_details: true })

            setTimeout(this.banner_id_to_name, 300)
            this.generateQrImage()

            setTimeout(() => {
                window.addEventListener('click', this.window_on_click_handler, {
                    once: true
                })
            }, 300)
        }
    }

    window_on_click_handler = () => {
        if (this.state.showing_details) {
            this.setState({ ...this.state, showing_details: false })

            setTimeout(this.banner_name_to_id, 300)
        }
    }

    componentWillUnmount(): void {
        window.removeEventListener('click', this.window_on_click_handler)
    }

    getCrossListingLabel = (): string | null => {
        const { canonical_course, cross_listed_as } = this.props.course
        if (canonical_course) {
            return `= ${canonical_course}`
        }
        if (cross_listed_as && cross_listed_as.length > 0) {
            return `= ${cross_listed_as.join(', ')}`
        }
        return null
    }

    render() {
        const { course_name, course_qr_code_url } = this.props.course
        const crossListingLabel = this.getCrossListingLabel()

        return (
            <div
                className={`${
                    this.state.showing_details
                        ? 'card-transluscent-active'
                        : 'card-transluscent'
                } hover:card-transluscent-hover w-42 h-64 duration-300 overflow-hidden flex justify-center items-center flex-col cursor-pointer select-none relative`}
                onClick={this.card_on_click_handler}
            >
                <Transition
                    show={this.state.showing_details}
                    enter="transition-transform duration-250"
                    enterFrom="-translate-y-full"
                    enterTo="translate-0"
                    leave="transition-transform duration-250"
                    leaveFrom="translate-0"
                    leaveTo="-translate-y-full"
                    className="top-0 relative mx-auto pb-5"
                >
                    {/* Hidden canvas for generating saveable image */}
                    <div ref={this.canvasRef} className="hidden">
                        <QRCodeCanvas
                            value={course_qr_code_url}
                            size={400}
                            bgColor="#ffffff"
                            fgColor="#212121"
                        />
                    </div>
                    {/* Visible: SVG on desktop, long-press-saveable img on mobile */}
                    {this.state.qrImageUrl ? (
                        <img
                            src={this.state.qrImageUrl}
                            alt={`QR code for ${course_name}`}
                            className="mx-auto mt-4 w-[84%]"
                        />
                    ) : (
                        <QRCodeSVG
                            className="mx-auto text-logo dark:text-logo-dark mt-4 w-[84%]"
                            value={course_qr_code_url}
                            size={200}
                            bgColor="transparent"
                            fgColor="#212121"
                        />
                    )}
                </Transition>

                <Transition
                    as="div"
                    show={!this.state.showing_details}
                    enter="transition-opacity duration-150"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="transition-opacity duration-150"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                    className="absolute text-center"
                >
                    <h1 className="text-lg font-bold">
                        {this.SHOW_ID
                            ? course_name
                            : course_name.replace('/', ' / ')}
                    </h1>
                    {crossListingLabel && (
                        <p className="text-xs text-gray-500 font-medium mt-1 truncate px-2">
                            {crossListingLabel}
                        </p>
                    )}
                </Transition>

                <span
                    className={`${this.props.school.classes.bg} w-full h-6 absolute bottom-0 text-center ${
                        this.state.showing_details
                            ? 'opacity-1'
                            : 'opacity-stroke'
                    } transition-opacity duration-150 text-white font-bold`}
                >
                    {this.state.banner}
                </span>
            </div>
        )
    }
}

export default CourseCard
